import React, { useState } from 'react'
import { Wrench, RefreshCw, AlertCircle, ShieldAlert, CheckCircle2 } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { clearMaintenanceMode, setMaintenanceMode } from '../../store/appSlice'
import { checkSystemStatus } from '../../api/system'

export const MaintenancePage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { maintenanceMessage, lastChecked } = useAppSelector((state) => state.app)
  const [checking, setChecking] = useState(false)
  const [statusFeedback, setStatusFeedback] = useState<string | null>(null)

  const handleRetry = async () => {
    setChecking(true)
    setStatusFeedback(null)
    try {
      const res = await checkSystemStatus()
      if (res.status === 'ok' && !res.maintenance) {
        dispatch(clearMaintenanceMode())
        setStatusFeedback('System is operational! Redirecting...')
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        dispatch(
          setMaintenanceMode({
            isMaintenance: true,
            message: res.message || 'System is still undergoing maintenance.',
          })
        )
        setStatusFeedback('Server is still under maintenance.')
      }
    } catch (err: any) {
      setStatusFeedback(err.message || 'Unable to reach backend server.')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background Glow Accents */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-orange-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-xl w-full bg-slate-800/80 backdrop-blur-xl border border-slate-700/60 rounded-2xl p-8 shadow-2xl z-10 text-center flex flex-col items-center">
        {/* Animated Maintenance Icon Container */}
        <div className="relative mb-6">
          <div className="w-20 h-20 bg-amber-500/20 border border-amber-500/30 rounded-2xl flex items-center justify-center text-amber-400 shadow-inner">
            <Wrench className="w-10 h-10 animate-pulse" />
          </div>
          <div className="absolute -top-1 -right-1 bg-amber-500 text-slate-950 p-1 rounded-full shadow">
            <AlertCircle className="w-4 h-4" />
          </div>
        </div>

        {/* Title & Badge */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-4">
          <ShieldAlert className="w-3.5 h-3.5" /> Scheduled Maintenance
        </div>

        <h1 className="text-3xl font-extrabold tracking-tight text-white mb-3">
          Gowardhan ERP Under Maintenance
        </h1>

        <p className="text-slate-300 text-sm leading-relaxed mb-6">
          {maintenanceMessage ||
            'We are currently updating and optimizing our enterprise services to serve you better. Please check back shortly.'}
        </p>

        {/* Retry Status Box */}
        {statusFeedback && (
          <div
            className={`w-full mb-6 p-3 rounded-xl text-xs font-medium flex items-center justify-center gap-2 border ${
              statusFeedback.includes('operational')
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-amber-500/10 border-amber-500/30 text-amber-300'
            }`}
          >
            {statusFeedback.includes('operational') ? (
              <CheckCircle2 className="w-4 h-4" />
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
            {statusFeedback}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 w-full">
          <button
            onClick={handleRetry}
            disabled={checking}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm transition-all duration-200 shadow-lg shadow-amber-500/20 disabled:opacity-50 cursor-pointer active:scale-95"
          >
            <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
            {checking ? 'Checking Status...' : 'Check Status & Retry'}
          </button>
        </div>

        {/* Last Checked Footer */}
        {lastChecked && (
          <p className="text-xs text-slate-500 mt-6">
            Last server check attempt: {new Date(lastChecked).toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Footer Branding */}
      <footer className="mt-8 text-center text-xs text-slate-500 z-10">
        &copy; {new Date().getFullYear()} Gowardhan ERP. All Rights Reserved.
      </footer>
    </div>
  )
}

export default MaintenancePage
